import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { Search, Clock } from 'lucide-react';
import { supabase } from '../supabaseClient';
import './Blog.css';

function truncateExcerpt(text, maxLen = 140) {
  if (!text || text.length <= maxLen) return text;
  const cut = text.lastIndexOf(' ', maxLen);
  return text.slice(0, cut > 0 ? cut : maxLen) + '…';
}

function estimateReadTime(text) {
  if (!text) return '3 min read';
  const words = text.trim().split(/\s+/).length;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min read`;
}

const STATIC_ARTICLES = [
  { id: 'rag-systems-enterprise-knowledge', title: 'How to Implement RAG Systems for Enterprise Knowledge Management', category: 'AI Development', date: 'March 20, 2026', excerpt: 'A comprehensive guide to building Retrieval Augmented Generation systems that actually work in production environments.' },
  { id: 'real-cost-ai-implementation', title: 'The Real Cost of AI Implementation: Beyond the Hype', category: 'AI Strategy', date: 'March 5, 2026', excerpt: 'Breaking down the actual costs, timelines, and ROI expectations for AI projects in 2024.' },
  { id: 'computer-vision-manufacturing', title: 'Computer Vision in Manufacturing: 5 Use Cases with Proven ROI', category: 'Computer Vision', date: 'February 13, 2026', excerpt: 'Real-world applications of AI-powered visual inspection systems in manufacturing environments.' },
  { id: 'building-vs-buying-ai', title: 'Building vs Buying: When to Use Off-the-Shelf AI vs Custom Solutions', category: 'AI Strategy', date: 'January 24, 2026', excerpt: 'A decision framework for choosing between existing AI tools and custom-built solutions.' },
  { id: 'agentic-ai-evolution', title: 'Agentic AI: The Next Evolution Beyond Chatbots', category: 'AI Development', date: 'January 4, 2026', excerpt: 'Understanding autonomous AI agents that can execute multi-step workflows without human intervention.' },
  { id: 'scale-engineering-team', title: 'How to Scale Your Engineering Team Without Hiring Full-Time', category: 'Staffing', date: 'December 15, 2025', excerpt: 'Strategies for augmenting your development capacity with remote dedicated teams.' },
  { id: 'llm-finetuning-vs-prompt-engineering', title: 'LLM Fine-Tuning vs Prompt Engineering: Which Approach is Right?', category: 'AI Development', date: 'November 25, 2025', excerpt: 'Comparing different approaches to customizing large language models for your use case.' },
  { id: 'digital-transformation-healthcare', title: 'Digital Transformation in Healthcare: AI Use Cases That Work', category: 'Industry Insights', date: 'November 5, 2025', excerpt: 'Practical AI applications in healthcare that deliver measurable improvements in patient care.' },
  { id: 'technical-debt-ai-projects', title: 'The Hidden Costs of Technical Debt in AI Projects', category: 'Engineering', date: 'October 16, 2025', excerpt: 'Why cutting corners in AI development leads to expensive problems down the road.' },
  { id: 'prototype-to-production', title: 'From Prototype to Production: Deploying AI Systems at Scale', category: 'DevOps', date: 'September 26, 2025', excerpt: 'Best practices for taking AI models from development to production-ready systems.' },
];

const ALL_CATEGORIES = ['All', ...Array.from(new Set(STATIC_ARTICLES.map(a => a.category)))];

function SkeletonCard() {
  return (
    <div className="article-card skeleton-card" aria-hidden="true">
      <div className="skeleton-chip" />
      <div className="skeleton-title" />
      <div className="skeleton-title skeleton-title-short" />
      <div className="skeleton-text" />
      <div className="skeleton-text skeleton-text-short" />
      <div className="skeleton-footer" />
    </div>
  );
}

function Blog() {
  usePageTitle('Blog');
  const [dbArticles, setDbArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    supabase
      .from('blogs')
      .select('*')
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (data) setDbArticles(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const allArticles = useMemo(() => [
    ...dbArticles.map(b => ({
      id: b.id,
      title: b.title,
      author: b.author,
      category: null,
      date: b.date,
      excerpt: truncateExcerpt(b.content),
      readTime: estimateReadTime(b.content),
      isDb: true,
    })),
    ...STATIC_ARTICLES.map(a => ({
      ...a,
      readTime: estimateReadTime(a.excerpt + ' ' + a.excerpt),
      isDb: false,
    })),
  ], [dbArticles]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return allArticles.filter(a => {
      const matchesSearch = !q
        || a.title.toLowerCase().includes(q)
        || (a.excerpt || '').toLowerCase().includes(q)
        || (a.category || '').toLowerCase().includes(q)
        || (a.author || '').toLowerCase().includes(q);
      const matchesCategory = activeCategory === 'All' || a.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [allArticles, search, activeCategory]);

  return (
    <div className="blog">
      <section className="blog-hero">
        <div className="blog-container">
          <h1 className="blog-hero-title">Insights &amp; Blog</h1>
          <p className="blog-hero-subtitle">Practical insights on AI adoption and digital transformation</p>
        </div>
      </section>

      <section className="blog-filters">
        <div className="blog-container">
          <div className="blog-search-row">
            <div className="blog-search-wrap">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                className="blog-search"
                placeholder="Search articles…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="category-chips">
            {ALL_CATEGORIES.map(cat => (
              <button
                key={cat}
                className={`category-chip ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="blog-main">
        <div className="blog-container">
          {loading ? (
            <div className="articles-grid">
              {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="blog-empty">
              <p>No articles match your search. <button className="link-btn" onClick={() => { setSearch(''); setActiveCategory('All'); }}>Clear filters</button></p>
            </div>
          ) : (
            <div className="articles-grid">
              {filtered.map((article, index) => (
                <article key={article.id || index} className="article-card">
                  <div className="article-card-top">
                    {article.category ? (
                      <span className="article-category">{article.category}</span>
                    ) : article.author ? (
                      <span className="article-category article-category-author">By {article.author}</span>
                    ) : null}
                  </div>
                  <h2 className="article-title">{article.title}</h2>
                  <p className="article-excerpt">{article.excerpt}</p>
                  <div className="article-footer">
                    <div className="article-meta-row">
                      <span className="article-date">{article.date}</span>
                      <span className="article-read-time"><Clock size={12} />{article.readTime}</span>
                    </div>
                    <Link to={`/blog/${article.id}`} className="article-link">Read More →</Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default Blog;
